/**
 * DPL DEW — Settings Service
 *
 * Single source of truth for all app-level configuration.
 * All industry-specific defaults (estimate types, scope group types,
 * payment schedule rules, terms templates) live here — not in source code.
 *
 * AppPreferences drives what the UI shows, making the app domain-agnostic.
 */

import { supabase } from '@/integrations/supabase/client';
import type { PaymentScheduleRule } from '@/utils/financials';
import type { ScopeGroupDefinition } from '@/types/quotation';
import type { Json } from '@/integrations/supabase/types';
import { DEFAULT_PAYMENT_MILESTONES } from '@/types/quotation';

interface TenantSettingRow {
  key: string;
  value: unknown;
}

// ─── Settings types ────────────────────────────────────────────────────────────

export interface CompanyProfile {
  id: string;
  name: string;
  legal_name?: string;
  phone: string;
  email?: string;
  address: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  gst_number?: string;
  logo_url?: string;
  authorized_signatory?: string;
  /** Industry slugs this company operates in. Single for starter, multiple for higher plans. */
  industries?: string[];
  created_at: string;
  updated_at: string;
}

export interface AppPreferences {
  id: string;
  // Quote numbering
  default_validity_days: number;
  default_gst_rate: number;
  currency: string;
  date_format: string;
  quote_number_prefix: string;
  quote_number_start: number;

  // ── Domain configuration (catalogue-driven) ──────────────────────────────
  /** Selectable estimate types. Any business can define their own. */
  estimate_types: { value: string; label: string }[];

  /** Scope group types (interior = rooms, IT = modules, events = zones, etc.) */
  scope_group_types: ScopeGroupDefinition[];

  /** Section work-type options shown in the section editor */
  section_work_types: { value: string; label: string }[];

  /** Payment schedule rules. Applied in order by grandTotal bracket. */
  payment_schedule_rules: PaymentScheduleRule[];

  /** Default milestone structure (used when no rules match or rules are empty) */
  default_payment_schedule: Omit<import('@/types/quotation').PaymentMilestone, 'id' | 'amount'>[];

  /** Default terms template (all arrays, populated by settings UI) */
  default_terms?: {
    materialsUsed: string[];
    inclusions: string[];
    exclusions: string[];
    generalTerms: string[];
    warranty: string[];
  };

  created_at: string;
  updated_at: string;
}

// ─── Sensible defaults (truly generic — no interior specifics) ────────────────

const DEFAULT_COMPANY: Omit<CompanyProfile, 'id' | 'created_at' | 'updated_at'> = {
  name: 'Dcode Interiors',
  legal_name: 'DCODE Private Limited',
  phone: '+91 96338 60898',
  email: 'info@dcodeinteriors.com',
  address: 'KC Arcade, 2nd Floor, Near TV Centre, CSEZ, Ernakulam, Kerala – 682037',
  city: 'Ernakulam',
  state: 'Kerala',
  country: 'India',
  pincode: '682037',
  gst_number: '',
  logo_url: '',
  authorized_signatory: 'Authorized Signatory',
};

const DEFAULT_PREFERENCES: Omit<AppPreferences, 'id' | 'created_at' | 'updated_at'> = {
  default_validity_days: 30,
  default_gst_rate: 18,
  currency: 'INR',
  date_format: 'dd/MM/yyyy',
  quote_number_prefix: 'QT',
  quote_number_start: 1000,

  // These start empty — the first-run settings wizard fills them in.
  // Interior companies would add: full_interior, modular_kitchen, etc.
  // IT companies would add: software_dev, maintenance, consulting, etc.
  estimate_types: [],

  // Interior default scope groups (rooms) — can be fully reconfigured.
  scope_group_types: [
    { value: 'general', label: 'General',    allowMultiple: false },
    { value: 'custom',  label: 'Custom Scope', allowMultiple: true },
  ],

  section_work_types: [
    { value: 'standard', label: 'Standard Work' },
    { value: 'custom',   label: 'Custom Work'   },
  ],

  // No hardcoded payment rules — proportional split by default.
  payment_schedule_rules: [],

  default_payment_schedule: DEFAULT_PAYMENT_MILESTONES,

  default_terms: {
    materialsUsed: [],
    inclusions: [],
    exclusions: [],
    generalTerms: [],
    warranty: [],
  },
};

// ─── Service ──────────────────────────────────────────────────────────────────

class SettingsService {
  private companyCache: CompanyProfile | null = null;
  private prefsCache: AppPreferences | null = null;
  public rawIdentityCache: any = null;
  private initialized = false;
  private _initPromise: Promise<void> | null = null;
  private currentTenantId: string | null = null;
  private _initVersion = 0; // incremented on resetCache to bust stale in-flight promises

  async initialize(): Promise<void> {
    // 1. Safety check: Don't attempt to sync if no session exists (avoids 401s on login)
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      this._setDefaults();
      return;
    }

    const tenantId = await this.getTenantId();
    if (this.initialized && this.currentTenantId === tenantId) return;
    if (this._initPromise) return this._initPromise;

    if (this.currentTenantId !== tenantId) {
      this.resetCache();
      this.currentTenantId = tenantId;
    }

    // Populate defaults immediately so synchronous getters never return null
    this._setDefaults();

    const versionAtStart = this._initVersion;
    this._initPromise = this._syncFromRemote(versionAtStart).finally(() => {
      // Only mark initialized if no resetCache() happened mid-flight
      if (this._initVersion === versionAtStart) {
        this.initialized = true;
      }
      this._initPromise = null;
    });

    return this._initPromise;
  }

  private _setDefaults(): void {
    const now = new Date().toISOString();
    if (!this.companyCache) {
      this.companyCache = { ...DEFAULT_COMPANY, id: 'default', created_at: now, updated_at: now } as CompanyProfile;
    }
    if (!this.prefsCache) {
      this.prefsCache = { ...DEFAULT_PREFERENCES, id: 'default', created_at: now, updated_at: now } as AppPreferences;
    }
  }

  private async _syncFromRemote(expectedVersion?: number): Promise<void> {
    try {
      const tenantId = await this.getTenantId();
      if (!tenantId) return;

      const { data, error } = await supabase
        .from('tenant_settings')
        .select('key, value')
        .eq('tenant_id', tenantId);

      if (error || !data) return;

      const settingsRows = data as TenantSettingRow[];
      const remoteCompany = settingsRows.find((d) => d.key === 'company_profile')?.value as Partial<CompanyProfile> | undefined;
      const remotePrefs = settingsRows.find((d) => d.key === 'app_preferences')?.value as Partial<AppPreferences> | undefined;
      const remoteIdentity = settingsRows.find((d) => d.key === 'business_identity')?.value as any;

      if (remoteCompany) this.companyCache = { ...remoteCompany } as CompanyProfile;
      if (remotePrefs)   this.prefsCache   = { ...DEFAULT_PREFERENCES, ...remotePrefs, id: remotePrefs.id || 'default' } as AppPreferences;
      
      this.rawIdentityCache = remoteIdentity || null;
    } catch {
      // Network failure — keep defaults silently
    }
  }

  async saveToRemote(key: string, value: unknown): Promise<void> {
    const tenantId = await this.getTenantId();
    if (!tenantId) throw new Error('No tenant available for settings save');

    // Immediately keep in-memory caches in sync to prevent stale identity overwrites
    if (key === 'business_identity') {
      this.rawIdentityCache = value;
    } else if (key === 'company_profile') {
      this.companyCache = value as CompanyProfile;
    } else if (key === 'app_preferences') {
      this.prefsCache = value as AppPreferences;
    }

    // Try new RLS-bypassing function first
    const { error } = await supabase.rpc('save_tenant_setting' as any, {
      tenant_uuid: tenantId,
      setting_key: key,
      setting_value: value
    } as any);
    
    if (!error) return;
    
    // Fallback to direct access if function doesn't work
    const { error: fallbackError } = await supabase
      .from('tenant_settings')
      .upsert(
        { tenant_id: tenantId, key, value: value as Json, updated_at: new Date().toISOString() },
          { onConflict: 'tenant_id,key' }
        );
    if (fallbackError) throw fallbackError;
  }

  // ── Getters ────────────────────────────────────────────────────────────────

  getCompanyProfile(): CompanyProfile {
    const now = new Date().toISOString();
    return this.companyCache ?? { ...DEFAULT_COMPANY, id: 'default', created_at: now, updated_at: now } as CompanyProfile;
  }

  getPreferences(): AppPreferences {
    const now = new Date().toISOString();
    return this.prefsCache ?? { ...DEFAULT_PREFERENCES, id: 'default', created_at: now, updated_at: now } as AppPreferences;
  }

  /** Convenience: returns scope group definitions for the scope picker UI */
  getScopeGroupTypes(): ScopeGroupDefinition[] {
    return this.getPreferences().scope_group_types ?? [];
  }

  /** Convenience: returns estimate type options for the estimate type picker */
  getEstimateTypes(): { value: string; label: string }[] {
    return this.getPreferences().estimate_types ?? [];
  }

  /** Convenience: returns payment schedule rules for the financial calculator */
  getPaymentScheduleRules(): import('@/utils/financials').PaymentScheduleRule[] {
    return this.getPreferences().payment_schedule_rules ?? [];
  }

  // ── Updaters ───────────────────────────────────────────────────────────────

  async updateCompanyProfile(
    updates: Partial<Omit<CompanyProfile, 'id' | 'created_at' | 'updated_at'>>
  ): Promise<CompanyProfile> {
    const current = this.getCompanyProfile();
    const next = { ...current, ...updates, updated_at: new Date().toISOString() };
    this.companyCache = next;
    await this.saveToRemote('company_profile', next);

    // Sync to business_identity to keep them in sync
    try {
      const raw = this.rawIdentityCache;
      const defaultIdentity = {
        branding_version: 1,
        company_name: 'My Workspace',
        document_theme: 'minimal',
        density: 'comfortable',
        typography_preset: 'inter',
        theme_mode: 'light',
        print_preferences: {
          paper_size: 'A4',
          show_watermark: false,
          show_qr_payment: false
        }
      };
      const nextIdentity = {
        ...defaultIdentity,
        ...(raw || {}),
        company_name: next.name || (raw?.company_name) || defaultIdentity.company_name,
        legal_name: next.legal_name || (raw?.legal_name) || next.name,
        contact_phone: next.phone || (raw?.contact_phone),
        contact_email: next.email || (raw?.contact_email),
        address: next.address || (raw?.address),
        gstin: next.gst_number || (raw?.gstin),
        logo_url: next.logo_url || (raw?.logo_url)
      };
      this.rawIdentityCache = nextIdentity;
      await this.saveToRemote('business_identity', nextIdentity);
    } catch (syncErr) {
      console.warn('Failed to sync company profile to business identity:', syncErr);
    }

    return next;
  }

  async updatePreferences(
    updates: Partial<Omit<AppPreferences, 'id' | 'created_at' | 'updated_at'>>
  ): Promise<AppPreferences> {
    const current = this.getPreferences();
    const next = { ...current, ...updates, updated_at: new Date().toISOString() };
    this.prefsCache = next;
    await this.saveToRemote('app_preferences', next);
    return next;
  }

  async getNextQuoteNumber(): Promise<string> {
    const prefs = this.getPreferences();
    const nextStart = prefs.quote_number_start + 1;
    await this.updatePreferences({ quote_number_start: nextStart });
    return `${prefs.quote_number_prefix || 'EST'}-${nextStart}`;
  }

  async resetToDefaults(): Promise<void> {
    this._setDefaults();
    await Promise.all([
      this.saveToRemote('company_profile', this.companyCache),
      this.saveToRemote('app_preferences', this.prefsCache),
    ]);
  }

  resetCache(): void {
    this.companyCache = null;
    this.prefsCache = null;
    this.initialized = false;
    this._initPromise = null;
    this._initVersion++; // bust any in-flight _syncFromRemote
  }

  private async getTenantId(): Promise<string | null> {
    // Use the new current_tenant_id function that handles admin vs tenant properly
    const { data, error } = await supabase.rpc('current_tenant_id' as any);
    if (!error && data) return data;

    return null;
  }
}

export const settingsService = new SettingsService();
// Only initialize in browser context — prevents Supabase network calls in Node/Vitest
if (typeof window !== 'undefined') {
  settingsService.initialize().catch(console.error);
}
